// src/components/common/Header.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Menu, Music2, Sparkles, X } from 'lucide-react';
import { getHeaderLinks } from '../../utils/headerLinks';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';
import { getNotifications, markAllNotificationsRead, markNotificationRead } from '../../services/api';
import '../../styles/northernLights.css';

const LOGO_URL = '/EduVibzLogo-nav.png';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, userStatus, loading, logout } = useAuth();
  const navigate = useNavigate();

  const links = useMemo(() => {
    if (loading) {
      return getHeaderLinks('guest');
    }

    return getHeaderLinks(userStatus, user?.planId, user?.role);
  }, [loading, userStatus, user]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((open) => !open);
    setIsNotificationOpen(false);
  };

  useEffect(() => {
    if (loading || userStatus === 'guest') {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    let cancelled = false;
    getNotifications()
      .then((response) => {
        if (cancelled) return;
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unreadCount || 0);
      })
      .catch((error) => {
        logger.warn('Notifications unavailable', { error });
      });

    return () => {
      cancelled = true;
    };
  }, [loading, userStatus, user?.cognitoSub]);

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      const response = await markNotificationRead(notificationId);
      setNotifications((current) => current.map((notification) => (
        notification._id === notificationId ? { ...notification, read: true, readAt: response.data.notification?.readAt } : notification
      )));
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      logger.warn('Could not mark notification read', { error });
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((current) => current.map((notification) => ({ ...notification, read: true, readAt: notification.readAt || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      logger.warn('Could not mark notifications read', { error });
    }
  };

  const visibleNotifications = useMemo(
    () => notifications.filter((notification) => !notification.read),
    [notifications]
  );

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      logger.error('Logout failed', { error });
    }
  };

  const renderLink = (link, index, mobile = false) => {
    if (link.label === 'Logout') {
      return (
        <button
          key={index}
          onClick={() => {
            handleLogout();
            if (mobile) toggleMobileMenu();
          }}
          className={mobile
            ? 'w-full rounded-full bg-eduvibe-green px-4 py-3 text-center font-extrabold text-slate-950 transition duration-300 hover:bg-eduvibe-blue'
            : 'rounded-full bg-eduvibe-green px-5 py-2.5 text-sm font-extrabold text-slate-950 transition duration-300 hover:bg-eduvibe-blue'}
        >
          {link.label}
        </button>
      );
    }

    const isLogin = link.label === 'Login';
    const isRegister = link.label === 'Register' || link.label === 'Free Trial';
    const label = link.label === 'Register' ? 'Start learning' : link.label;

    return (
      <Link
        key={index}
        to={link.to}
        onClick={mobile ? toggleMobileMenu : undefined}
        className={mobile
          ? `block w-full rounded-full px-4 py-3 text-center font-semibold transition duration-300 ${
            isRegister
              ? 'bg-eduvibe-green text-slate-950 hover:bg-eduvibe-blue'
              : isLogin
                ? 'border border-eduvibe-green/70 text-eduvibe-green hover:bg-eduvibe-green hover:text-slate-950'
                : 'text-white hover:bg-white/10 hover:text-eduvibe-green'
          }`
          : `text-sm font-semibold transition duration-300 ${
            isRegister
              ? 'rounded-full bg-eduvibe-green px-5 py-2.5 text-slate-950 hover:bg-eduvibe-blue'
              : isLogin
                ? 'rounded-full border border-eduvibe-green/70 px-5 py-2.5 text-eduvibe-green hover:bg-eduvibe-green hover:text-slate-950'
                : 'text-white/90 hover:text-eduvibe-green'
          }`}
      >
        {label}
      </Link>
    );
  };

  const renderNotifications = (mobile = false) => {
    if (loading || userStatus === 'guest') return null;

    return (
      <div className={mobile ? 'relative' : 'relative'}>
        <button
          type="button"
          onClick={() => {
            setIsNotificationOpen((open) => !open);
            setIsMobileMenuOpen(false);
          }}
          className={mobile
            ? 'relative flex h-11 w-11 items-center justify-center rounded-full border border-eduvibe-green/35 text-white transition hover:bg-white/10 hover:text-eduvibe-green'
            : 'relative flex h-10 w-10 items-center justify-center rounded-full border border-eduvibe-green/35 text-white transition hover:bg-white/10 hover:text-eduvibe-green'}
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-eduvibe-green px-1 text-xs font-black text-slate-950">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {isNotificationOpen && (
          <div className={`${mobile ? 'fixed left-4 right-4 top-20' : 'absolute right-0 top-12 w-[23rem] max-w-[calc(100vw-2rem)]'} overflow-hidden rounded-[28px] border border-white/18 bg-[#071326] text-white shadow-[0_28px_90px_rgba(0,0,0,0.72),0_0_0_1px_rgba(45,249,166,0.08)_inset] ring-1 ring-eduvibe-green/10 backdrop-blur-2xl`}>
            <div className="absolute inset-x-0 top-0 h-28 bg-[radial-gradient(circle_at_20%_0%,rgba(45,249,166,0.22),transparent_36%),radial-gradient(circle_at_82%_16%,rgba(90,210,255,0.16),transparent_38%)]" aria-hidden="true" />
            <div className="relative flex items-center justify-between border-b border-white/10 px-4 py-3.5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-eduvibe-green/28 bg-eduvibe-green/12 shadow-[0_0_24px_rgba(45,249,166,0.16)]">
                  <Bell className="h-5 w-5 text-eduvibe-green" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-base font-black leading-tight text-white">Notifications</p>
                  <p className="text-xs font-semibold text-white/58">
                    {unreadCount ? `${unreadCount} new update${unreadCount === 1 ? '' : 's'}` : 'All caught up'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleMarkAllNotificationsRead}
                  disabled={!visibleNotifications.length}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-eduvibe-green/20 bg-eduvibe-green/10 text-eduvibe-green transition hover:bg-eduvibe-green hover:text-slate-950 disabled:cursor-not-allowed disabled:border-white/10 disabled:bg-white/5 disabled:text-white/30"
                  aria-label="Mark all notifications as read"
                  title="Mark all as read"
                >
                  <CheckCheck className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsNotificationOpen(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/72 transition hover:bg-white/12 hover:text-white"
                  aria-label="Close notifications"
                  title="Close"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="relative max-h-80 overflow-y-auto p-2.5">
              {visibleNotifications.length ? visibleNotifications.map((notification) => (
                <button
                  key={notification._id}
                  type="button"
                  onClick={() => handleMarkNotificationRead(notification._id)}
                  className="group mb-2 flex w-full items-start gap-3 rounded-3xl border border-eduvibe-green/18 bg-[linear-gradient(135deg,rgba(45,249,166,0.16),rgba(90,210,255,0.08)_48%,rgba(255,255,255,0.065))] p-3.5 text-left shadow-[0_16px_40px_rgba(0,0,0,0.28)] transition hover:-translate-y-0.5 hover:border-eduvibe-green/34 hover:bg-eduvibe-green/16 focus:outline-none focus:ring-2 focus:ring-eduvibe-green/45"
                  aria-label={`Dismiss notification: ${notification.title}`}
                >
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#071326]/84 text-eduvibe-green shadow-[0_0_0_1px_rgba(45,249,166,0.2)_inset]">
                    {notification.type === 'challenge' ? (
                      <Sparkles className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Music2 className="h-5 w-5" aria-hidden="true" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-black leading-snug text-white">{notification.title}</span>
                    <span className="mt-1 block text-sm font-semibold leading-snug text-white/72">{notification.message}</span>
                  </span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/7 text-white/54 transition group-hover:border-eduvibe-green/35 group-hover:bg-eduvibe-green group-hover:text-slate-950">
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </span>
                </button>
              )) : (
                <div className="px-4 py-8 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-eduvibe-green/22 bg-eduvibe-green/10 text-eduvibe-green">
                    <CheckCheck className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <p className="mt-3 text-sm font-black text-white">No new notifications</p>
                  <p className="mt-1 text-xs font-semibold text-white/55">Dismissed updates stay out of the way.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="ev-fade-up fixed left-0 right-0 top-0 z-50 min-h-[76px] border-b border-eduvibe-blue/10 bg-[#020817]/90 text-white backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <nav className="flex justify-between items-center py-3 md:py-4">
          <div className="flex items-center min-w-0">
            <Link to="/" className="text-2xl font-bold flex items-center gap-2 min-w-0" aria-label="EduVibz home">
              <picture>
                <source srcSet="/EduVibzLogo-nav.avif" type="image/avif" />
                <source srcSet="/EduVibzLogo-nav.webp" type="image/webp" />
                <img
                  src={LOGO_URL}
                  alt=""
                  width="206"
                  height="115"
                  className="h-14 w-auto max-w-[154px] object-contain md:h-16 md:max-w-[172px]"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              </picture>
              <span className="sr-only">EduVibz</span>
            </Link>
          </div>
          <div className="hidden items-center gap-5 md:flex">
            {links.map((link, index) => renderLink(link, index))}
            {renderNotifications()}
            {loading && (
              <span className="text-xs text-eduvibe-green/70 animate-pulse">
                Checking session...
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 pr-1 md:hidden">
            {renderNotifications(true)}
            <button
              onClick={toggleMobileMenu}
              className="text-white focus:outline-none p-3 rounded-full hover:bg-eduvibe-blue/20"
              aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </div>
      {isMobileMenuOpen && (
        <div className="z-20 border-t border-eduvibe-blue/10 bg-[#020817]/95 backdrop-blur-xl md:hidden">
          <div className="mx-auto max-w-7xl space-y-3 px-4 py-4">
            {links.map((link, index) => renderLink(link, index, true))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
